import QuestionMarkIcon from '@heroicons/react/24/solid/QuestionMarkCircleIcon';
import { SvgIcon } from '@mui/material';
import type { NavEntryGroup, NavEntryItem } from '@starjack/layout';
import { Permission } from '@/models/user';
import { isDevelopment } from '@/constants';
import {
    BuildingOfficeIcon,
    BuildingStorefrontIcon,
    CreditCardIcon,
    DocumentDuplicateIcon,
    DocumentTextIcon,
    EnvelopeIcon,
    UserGroupIcon,
    UsersIcon,
    PresentationChartLineIcon,
    TicketIcon,
    ComputerDesktopIcon,
    ReceiptPercentIcon,
    SparklesIcon,
} from '@heroicons/react/24/solid';

export type BackofficeNavEntryItem = Omit<
    NavEntryItem,
    'permissionsRequired'
> & {
    permissionsRequired: Permission[];
};
export type BackofficeNavEntryGroup = Omit<NavEntryGroup, 'entries'> & {
    entries: BackofficeNavEntryItem[];
};

export const items: (BackofficeNavEntryGroup | BackofficeNavEntryItem)[] = [
    {
        title: 'Kunden',
        path: '/kunden',
        icon: (
            <SvgIcon fontSize='small'>
                <UsersIcon />
            </SvgIcon>
        ),
        permissionsRequired: [],
    },
    {
        title: 'Tickets',
        path: '/tickets',
        icon: (
            <SvgIcon fontSize='small'>
                <TicketIcon />
            </SvgIcon>
        ),
        permissionsRequired: [],
    },
    {
        title: 'Gutscheine',
        path: '/vouchers',
        icon: (
            <SvgIcon fontSize='small'>
                <ReceiptPercentIcon />
            </SvgIcon>
        ),
        permissionsRequired: [],
    },
    {
        title: 'Bergbahnen',
        path: '/bergbahnen',
        icon: (
            <SvgIcon fontSize='small'>
                <BuildingOfficeIcon />
            </SvgIcon>
        ),
        permissionsRequired: [Permission.ADMIN],
    },
    {
        title: 'Foto-Kalibrierung',
        path: '/foto-kalibrierung',
        icon: (
            <SvgIcon fontSize='small'>
                <BuildingOfficeIcon />
            </SvgIcon>
        ),
        permissionsRequired: [Permission.ADMIN],
    },

    {
        title: 'Auswertungen',
        path: '/auswertungen',
        icon: (
            <SvgIcon fontSize='small'>
                <PresentationChartLineIcon />
            </SvgIcon>
        ),
        permissionsRequired: [Permission.SUPPORTER],
    },

    {
        title: 'Coupons',
        path: '/coupons',
        icon: (
            <SvgIcon fontSize='small'>
                <DocumentTextIcon />
            </SvgIcon>
        ),
        permissionsRequired: [Permission.ADMIN],
    },

    {
        title: 'FAQs',
        path: '/faq',
        icon: (
            <SvgIcon fontSize='small'>
                <QuestionMarkIcon />
            </SvgIcon>
        ),
        permissionsRequired: [Permission.ADMIN],
    },

    {
        title: 'Textersetzungen',
        path: '/text-ersetzungen',
        icon: (
            <SvgIcon fontSize='small'>
                <DocumentTextIcon />
            </SvgIcon>
        ),
        permissionsRequired: [Permission.ADMIN],
    },

    {
        title: 'Webshops',
        path: '/webshops',
        icon: (
            <SvgIcon fontSize='small'>
                <UserGroupIcon />
            </SvgIcon>
        ),
        permissionsRequired: [Permission.ADMIN],
    },

    {
        title: 'Agenturzugänge',
        path: '/agenturen',
        icon: (
            <SvgIcon fontSize='small'>
                <BuildingStorefrontIcon />
            </SvgIcon>
        ),
        permissionsRequired: [Permission.ADMIN],
    },

    {
        title: 'Kassen',
        path: '/cash-desks',
        icon: (
            <SvgIcon fontSize='small'>
                <ComputerDesktopIcon />
            </SvgIcon>
        ),
        permissionsRequired: [Permission.ADMIN],
    },

    {
        title: 'Mails',
        path: '/mails',
        icon: (
            <SvgIcon fontSize='small'>
                <EnvelopeIcon />
            </SvgIcon>
        ),
        permissionsRequired: [Permission.ADMIN],
    },

    {
        title: 'Gutscheinvorlagen',
        path: '/gutscheinvorlagen',
        icon: (
            <SvgIcon fontSize='small'>
                <DocumentDuplicateIcon />
            </SvgIcon>
        ),
        permissionsRequired: [Permission.ADMIN],
    },

    {
        title: 'Abrechnungen',
        path: '/abrechnungen',
        icon: (
            <SvgIcon fontSize='small'>
                <CreditCardIcon />
            </SvgIcon>
        ),
        permissionsRequired: [Permission.ADMIN],
    },

    {
        title: 'Produktion',
        path: '/produktion',
        icon: (
            <SvgIcon fontSize='small'>
                <PresentationChartLineIcon />
            </SvgIcon>
        ),
        permissionsRequired: [Permission.ADMIN],
    },

    // A group, not four flat entries: they are one subject. `NavEntryGroup`
    // has no `path` of its own — it is a collapsible header, so the first
    // child owns `/ki-einstellungen`.
    // Assistant is not released yet: staging and review environments only.
    ...(isDevelopment
        ? [
              {
                  title: 'KI-Einstellungen',
                  icon: (
                      <SvgIcon fontSize='small'>
                          <SparklesIcon />
                      </SvgIcon>
                  ),
                  entries: [
                      // Hidden for now, may come back. Page and route stay in place.
                      // {
                      //     title: 'Verhalten',
                      //     path: '/ki-einstellungen',
                      //     icon: null,
                      //     permissionsRequired: [Permission.ADMIN],
                      //     // Exact, or the parent lights up on all three siblings —
                      //     // every one of them is prefixed by this path.
                      //     exact: true,
                      // },
                      {
                          title: 'Seiteninformationen',
                          path: '/ki-einstellungen/seiten',
                          icon: null,
                          permissionsRequired: [Permission.ADMIN],
                      },
                      {
                          title: 'Vorgeschlagene Fragen',
                          path: '/ki-einstellungen/fragen',
                          icon: null,
                          permissionsRequired: [Permission.ADMIN],
                      },
                      {
                          title: 'Chatverläufe',
                          path: '/ki-einstellungen/chats',
                          icon: null,
                          permissionsRequired: [Permission.ADMIN],
                      },
                  ],
              },
          ]
        : []),
];
